import React from 'react';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Type, BookOpen, Eye, Ear, Hand, MessageCircle,
  Monitor, CheckCircle, Keyboard, Globe,
} from 'lucide-react';

const features = [
  {
    icon: Type,
    title: 'Adjustable Text Size',
    desc: 'Choose between Small, Medium, Large, or Extra Large text from the user menu. Your preference is saved to your profile and applies across the entire platform.',
    color: '#20acf6',
  },
  {
    icon: BookOpen,
    title: 'Video Transcripts',
    desc: 'Every video element includes a "Transcript" button that reveals a full text version of the video content. This supports learners who are deaf, hard of hearing, or simply prefer to read.',
    color: '#02d1d4',
  },
  {
    icon: Ear,
    title: 'Audio Transcripts & Downloads',
    desc: 'Audio elements have viewable and downloadable text transcripts, so you can follow along or review offline at your own pace.',
    color: '#02d1d4',
  },
  {
    icon: Monitor,
    title: 'Closed Captions',
    desc: 'Videos support .vtt and .srt caption files for closed captions. Course creators can upload caption files through the course builder.',
    color: '#2a3e5d',
  },
  {
    icon: Eye,
    title: 'Learning Style Indicators',
    desc: 'Every course element displays a coloured icon showing its learning style: Visual (blue), Auditory (teal), Reading/Writing (dark blue), or Kinesthetic (green). This helps you identify content that matches your preferred way of learning.',
    color: '#20acf6',
  },
  {
    icon: Hand,
    title: 'Learning Style Quiz',
    desc: 'On your first login, an optional 5-question quiz identifies your preferred learning style. Results are saved to your profile and help you understand which elements will suit you best.',
    color: '#10B981',
  },
  {
    icon: MessageCircle,
    title: 'Ellie Chat Assistant',
    desc: 'Our friendly chatbot Ellie is available on every page via the blue chat icon in the bottom-right corner. She can answer common questions about pricing, courses, certificates, and technical issues. If she cannot help, she will connect you with our support team.',
    color: '#20acf6',
  },
  {
    icon: Keyboard,
    title: 'Keyboard Navigation',
    desc: 'The platform supports full keyboard navigation. All interactive elements have visible focus indicators, and you can tab through the interface without a mouse.',
    color: '#2a3e5d',
  },
  {
    icon: Globe,
    title: 'Responsive Design',
    desc: 'The platform works on desktops, tablets, and mobile phones. All course content adapts to your screen size without losing functionality.',
    color: '#02d1d4',
  },
];

const AccessibilityPage = () => {
  return (
    <div className="min-h-screen flex flex-col bg-[#F8FAFC]" data-testid="accessibility-page">
      <Header />

      <main className="flex-1">
        {/* Hero */}
        <section className="py-16 lg:py-20 bg-gradient-to-b from-[#f8fafc] to-[#F8FAFC]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <Badge className="bg-[#20acf6]/15 text-[#2a3e5d] mb-4">Accessibility</Badge>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#2a3e5d] mb-4">
              Learning for <span className="text-[#20acf6]">Everyone</span>
            </h1>
            <p className="text-base lg:text-lg text-[#1e293b]/70 max-w-2xl mx-auto leading-relaxed">
              Common Impact E-learning is committed to making training accessible to all care workers, regardless of ability, learning style, or device. Here is how we do it.
            </p>
          </div>
        </section>

        {/* Features Grid */}
        <section className="py-16 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f, i) => (
              <Card key={i} className="border-slate-200 hover:shadow-md transition-shadow bg-white">
                <CardContent className="p-6">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: `${f.color}15` }}>
                    <f.icon className="h-5 w-5" style={{ color: f.color }} />
                  </div>
                  <h3 className="text-base font-semibold text-[#1e293b] mb-2">{f.title}</h3>
                  <p className="text-sm text-[#1e293b]/60 leading-relaxed">{f.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Commitment Section */}
        <section className="py-16 bg-white">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-[#2a3e5d] mb-6 text-center">Our Commitment</h2>
            <div className="space-y-4">
              {[
                'We follow WCAG 2.1 AA guidelines as our baseline standard.',
                'All interactive elements have visible focus states for keyboard users.',
                'Colour is never the sole indicator of meaning — we use icons and labels alongside.',
                'We test with screen readers and keyboard-only navigation.',
                'We welcome feedback on accessibility — please email info@commonimpact.co.uk with any suggestions.',
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-[#10B981] shrink-0 mt-0.5" />
                  <p className="text-sm text-[#1e293b]/80 leading-relaxed">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default AccessibilityPage;
